import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  GUEST_SHARE_DENIED,
  guestShareWasBlocked,
} from "../../lib/guest-share";
import {
  adoptOAuthHandoff,
  clearOAuthNonce,
} from "../../lib/oauth-handoff";
import { useAuth } from "../../lib/useAuth";

export default function OauthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  // Same rule as SheetsConnect: a guest has isAuthenticated true, but must
  // still be allowed to finish Google sign-up and leave the guest session.
  const { user, guest } = useAuth();
  const alreadySignedIn = Boolean(user) && !guest;
  const [failed, setFailed] = useState(false);
  const params = new URLSearchParams(location.search);
  const invite = params.get("invite")?.trim() ?? "";
  const ticket = params.get("ticket");
  // TODO(remove-after-rollout): legacy JWT-in-URL from older API callbacks.
  const token = params.get("token");
  /**
   * Handle one redirect once. Redeeming the ticket writes `user` into the
   * auth store; if this effect re-ran on that update it would cancel the
   * in-flight navigate and take the already-signed-in branch to
   * `/data-sources`, skipping `pathAfterAuth` (e.g. `/setup`).
   */
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    // Snapshot from the first run only — do not list alreadySignedIn in deps.
    const signedInAtStart = alreadySignedIn;

    if (!ticket && !token) {
      // Drop any leftover start nonce so a later crafted ?token= cannot ride it.
      clearOAuthNonce();
      setFailed(true);
      return;
    }

    (async () => {
      // Drop secrets from the URL before adopting, so they never linger in the
      // address bar or in session history.
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}#/oauth`,
      );
      try {
        const handoff = await adoptOAuthHandoff({
          ticket,
          token,
          alreadySignedIn: signedInAtStart,
        });
        if (handoff.status === "failed") {
          setFailed(true);
          return;
        }
        if (handoff.status === "skipped" && signedInAtStart) {
          // Already signed in: a crafted link must not switch accounts. Land on
          // the invite hop or the default post-auth path without adopting.
          if (invite.length > 0) {
            navigate("/invite/" + invite, { replace: true });
          } else {
            navigate("/data-sources", { replace: true });
          }
          return;
        }
        if (handoff.status !== "adopted") {
          setFailed(true);
          return;
        }
        if (guestShareWasBlocked()) {
          toast.error(GUEST_SHARE_DENIED);
          navigate("/data-sources", { replace: true });
          return;
        }
        if (invite.length > 0) {
          navigate("/invite/" + invite, { replace: true });
        } else {
          navigate(handoff.next, { replace: true });
        }
      } catch {
        setFailed(true);
      }
    })();
    // alreadySignedIn intentionally omitted: the handoff must finish with the
    // signed-in state from this first run, not re-enter after adoptAuthToken.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket, token, invite, navigate]);

  if (failed) {
    return (
      <div className="min-h-screen bg-od-bg flex items-center justify-center px-4 py-8">
        <div
          role="alert"
          className="max-w-lg w-full space-y-5 bg-od-surface p-8 rounded-2xl shadow-lg border border-od-border text-center"
        >
          <div className="space-y-2">
            <h1 className="text-xl font-bold text-od-text">
              OAuth sign-in failed
            </h1>
            <p className="text-od-muted text-sm">
              We couldn&apos;t complete your sign-in. Please return and try
              again.
            </p>
          </div>
          <Link
            to={
              invite
                ? {
                    pathname: "/auth",
                    search: "?invite=" + encodeURIComponent(invite),
                  }
                : "/auth"
            }
            className="flex min-h-11 min-w-11 w-full items-center justify-center bg-od-primary hover:bg-od-primary-hover active:bg-od-primary-active text-od-primary-contrast py-2.5 px-6 rounded-lg text-base font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-surface"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-od-bg text-od-muted text-sm">
      Signing you in…
    </div>
  );
}
